import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/site/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { List, X, CaretDown } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const services = [
  { slug: "individual", name: "Individual Tax Filing" },
  { slug: "business", name: "Business Tax Filing" },
  { slug: "itin", name: "ITIN Application" },
  { slug: "fbar-fatca", name: "FBAR & FATCA" },
  { slug: "irs-notice", name: "IRS Notice Assistance" },
  { slug: "extensions", name: "Tax Extensions" },
  { slug: "corporate", name: "Corporate Programs" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const nav = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-[#0A2540]" : "text-slate-600 hover:text-[#0A2540]"}`;

  return (
    <header className="sticky top-0 z-50 tf-glass" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="shrink-0" data-testid="nav-home-logo">
            <Logo size={48} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" end className={nav} data-testid="nav-home">Home</NavLink>

            <DropdownMenu>
              <DropdownMenuTrigger className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="nav-services">
                Services <CaretDown size={12} weight="bold" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {services.map((s) => (
                  <DropdownMenuItem key={s.slug} onClick={() => navigate(`/services/${s.slug}`)} data-testid={`nav-service-${s.slug}`}>
                    {s.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={() => navigate("/services")} data-testid="nav-service-all">
                  <span className="font-semibold text-[#D32F2F]">View all services →</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to="/pricing" className={nav} data-testid="nav-pricing">Pricing</NavLink>
            <NavLink to="/refunds" className={nav} data-testid="nav-refunds">Refunds</NavLink>
            <NavLink to="/blog" className={nav} data-testid="nav-blog">Blog</NavLink>
            <NavLink to="/about" className={nav} data-testid="nav-about">About</NavLink>
            <NavLink to="/contact" className={nav} data-testid="nav-contact">Contact</NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user && user !== false ? (
              <>
                <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn">
                  Mission Control
                </Button>
                <Button variant="outline" onClick={logout} data-testid="nav-logout-btn">Log out</Button>
              </>
            ) : (
              <Button
                className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white px-6"
                onClick={() => navigate("/auth")}
                data-testid="nav-login-btn"
              >
                Login
              </Button>
            )}
          </div>

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 space-y-1 border-t border-slate-200/60 pt-3" data-testid="nav-mobile-panel">
            {[
              ["/", "Home"],
              ["/services", "Services"],
              ["/pricing", "Pricing"],
              ["/refunds", "Refunds"],
              ["/blog", "Blog"],
              ["/about", "About"],
              ["/contact", "Contact"],
            ].map(([to, label]) => (
              <NavLink key={to} to={to} end={to === "/"} className="block px-3 py-2 text-sm font-medium text-slate-700" onClick={() => setOpen(false)}>
                {label}
              </NavLink>
            ))}
            <div className="pt-3 flex gap-2 px-3">
              {user && user !== false ? (
                <>
                  <Button className="flex-1" variant="outline" onClick={() => { setOpen(false); navigate("/dashboard"); }}>Mission Control</Button>
                  <Button className="flex-1" onClick={() => { setOpen(false); logout(); }}>Log out</Button>
                </>
              ) : (
                <Button className="flex-1 bg-[#0A2540] text-white" onClick={() => { setOpen(false); navigate("/auth"); }} data-testid="mobile-login-btn">Login</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
